import { useState, useEffect, useRef, useCallback, useMemo, type ReactNode, type MutableRefObject } from "react";
import { createPortal } from "react-dom";
import type { GlobalIllumination } from "@roll1/shared";
import { toast } from "sonner";
import { useSessionContext } from "../../contexts/SessionContext";
import { useCanvasContext } from "../../contexts/CanvasContext";
import { useChatContext } from "../../contexts/ChatContext";
import { useI18n } from "../../i18n";
import { PageOpsProvider, type PageOpsContextValue } from "../../contexts/PageOpsContext";
import MapSettingsPopover from "../MapSettingsPopover";

interface PageManagerProps {
  saveCurrentPageToStore: () => void;
  loadPageToHooks: (pageData: any) => void;
  handleTokenUpload: () => void;
  /** Ref populated with the context value so parent can read page callbacks */
  pageOpsRef?: MutableRefObject<PageOpsContextValue | null>;
  children: ReactNode;
}

/**
 * PageManager — owns page CRUD callbacks, map settings state, file input.
 * Provides PageOpsContext for children (SidebarContent, DetachedPanelContent).
 * Renders MapSettingsPopover and hidden map file input.
 */
export default function PageManager({
  saveCurrentPageToStore,
  loadPageToHooks,
  handleTokenUpload,
  pageOpsRef,
  children,
}: PageManagerProps) {
  const { mp, outgoingPageId, pages, isGM } = useSessionContext();
  const { engine, globalIlluminationRef, dynamicFog } = useCanvasContext();
  const { chat } = useChatContext();
  const { t } = useI18n();

  // Own state (moved from AppShell)
  const [mapGridSize, setMapGridSize] = useState(
    () => engine.gridSizeRef?.current ?? 70,
  );
  const [mapScaleVal, setMapScaleVal] = useState(
    () => engine.mapScaleRef?.current ?? 1.0,
  );
  const [bgColor, setBgColor] = useState<string>("");
  const [illumination, setIllumination] = useState<GlobalIllumination['level']>('bright');
  const mapInputRef = useRef<HTMLInputElement>(null);

  // Sync map settings state when page changes
  useEffect(() => {
    const pid = pages.viewingPageId;
    if (!pid) return;
    const pd = pages.getPageData(pid);
    if (pd) {
      const gs = pd.gridSize ?? 70;
      const ms = pd.mapScale ?? 1.0;
      setMapGridSize(gs);
      setMapScaleVal(ms);
      setBgColor((pd as any).bgColor ?? "");
      setIllumination(pd.globalIllumination?.level ?? 'bright');
      if (engine.gridSizeRef) engine.gridSizeRef.current = gs;
      if (engine.mapScaleRef) engine.mapScaleRef.current = ms;
      if (engine.bgColorRef) engine.bgColorRef.current = (pd as any).bgColor ?? null;
    }
  }, [pages.viewingPageId]); // eslint-disable-line react-hooks/exhaustive-deps

  // File handler
  const handleMapUpload = useCallback(() => mapInputRef.current?.click(), []);

  const onMapFile = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      if (mp.session && mp.participant) {
        const form = new FormData();
        form.append("file", file);
        fetch(
          `/api/upload?sessionId=${encodeURIComponent(mp.session.id)}&participantId=${encodeURIComponent(mp.participant.id)}`,
          { method: "POST", body: form },
        )
          .then((r) => r.json())
          .then((data) => {
            if (data.url) {
              engine.loadMap(data.url);
              mp.send({
                type: "map:set",
                url: data.url,
                pageId: outgoingPageId,
              } as any);
              chat.addSystemMessage(`${t("toast.mapUploaded")} ${file.name}`);
              toast.success(`${t("toast.mapUploaded")} ${file.name}`);
            }
          })
          .catch(() => {
            chat.addSystemMessage(t("toast.mapUploadError"));
            toast.error(t("toast.mapUploadError"));
          });
      } else {
        const reader = new FileReader();
        reader.onload = (ev) => {
          engine.loadMap(ev.target!.result as string);
          chat.addSystemMessage(`${t("toast.mapUploaded")} ${file.name}`);
          toast.success(`${t("toast.mapUploaded")} ${file.name}`);
        };
        reader.readAsDataURL(file);
      }
      e.target.value = "";
    },
    [engine, chat, mp, outgoingPageId, t],
  );

  // Page callbacks (moved from AppShell)
  const handleCreatePage = useCallback(
    (name: string, parentPageId?: string) => {
      if (mp.session) mp.send({ type: "page:create", name, ...(parentPageId ? { parentPageId } : {}) } as any);
    },
    [mp],
  );
  const handleRenamePage = useCallback(
    (pageId: string, name: string) => {
      if (mp.session) mp.send({ type: "page:rename", pageId, name } as any);
    },
    [mp],
  );
  const handleDeletePage = useCallback(
    (pageId: string) => {
      if (mp.session) mp.send({ type: "page:delete", pageId } as any);
    },
    [mp],
  );
  const handleActivatePage = useCallback(
    (pageId: string) => {
      saveCurrentPageToStore();
      if (mp.session) mp.send({ type: "page:activate", pageId } as any);
    },
    [mp, saveCurrentPageToStore],
  );
  const handleReorderPages = useCallback(
    (pageIds: string[]) => {
      if (mp.session) mp.send({ type: "page:reorder", pageIds } as any);
    },
    [mp],
  );
  const handlePreviewPage = useCallback(
    (pageId: string) => {
      saveCurrentPageToStore();
      pages.previewPage(pageId);
    },
    [pages, saveCurrentPageToStore],
  );
  const handleReturnToActive = useCallback(() => {
    saveCurrentPageToStore();
    pages.returnToActive();
  }, [pages, saveCurrentPageToStore]);

  // Create context value
  const contextValue = useMemo<PageOpsContextValue>(
    () => ({
      saveCurrentPageToStore,
      loadPageToHooks,
      handleMapUpload,
      handleTokenUpload,
      pageCallbacks: {
        create: handleCreatePage,
        rename: handleRenamePage,
        delete: handleDeletePage,
        activate: handleActivatePage,
        preview: handlePreviewPage,
        returnToActive: handleReturnToActive,
        reorder: handleReorderPages,
      },
    }),
    [
      saveCurrentPageToStore,
      loadPageToHooks,
      handleMapUpload,
      handleTokenUpload,
      handleCreatePage,
      handleRenamePage,
      handleDeletePage,
      handleActivatePage,
      handlePreviewPage,
      handleReturnToActive,
      handleReorderPages,
    ],
  );

  // Expose context value to parent via ref
  if (pageOpsRef) pageOpsRef.current = contextValue;

  return (
    <PageOpsProvider value={contextValue}>
      {children}
      {isGM && engine.gridSizeRef && document.getElementById('zoom-controls') && createPortal(
          <MapSettingsPopover
            gridSize={mapGridSize}
            mapScale={mapScaleVal}
            bgColor={bgColor}
            globalIllumination={{ level: illumination }}
            mapUrl={(() => {
              const pid = pages.viewingPageId;
              if (!pid) return null;
              const pd = pages.getPageData(pid);
              return pd?.mapUrl ?? null;
            })()}
            onGridSizeChange={(size) => {
              setMapGridSize(size);
              engine.gridSizeRef.current = size;
              engine.markAllDirty();
              if (mp.session) {
                mp.send({
                  type: "page:update-settings",
                  pageId: pages.viewingPageId,
                  changes: { gridSize: size },
                } as any);
              }
            }}
            onMapScaleChange={(scale) => {
              setMapScaleVal(scale);
              engine.mapScaleRef.current = scale;
              engine.markAllDirty();
              if (mp.session) {
                mp.send({
                  type: "page:update-settings",
                  pageId: pages.viewingPageId,
                  changes: { mapScale: scale },
                } as any);
              }
            }}
            onBgColorChange={(color: string) => {
              setBgColor(color);
              engine.bgColorRef.current = color || null;
              // Update page data in client store immediately
              const pid = pages.viewingPageId;
              if (pid) {
                const pd = pages.getPageData(pid);
                if (pd) (pd as any).bgColor = color || undefined;
              }
              engine.markAllDirty();
              if (mp.session) {
                mp.send({
                  type: "page:update-settings",
                  pageId: pages.viewingPageId,
                  changes: { bgColor: color || undefined },
                } as any);
              }
            }}
            onGlobalIlluminationChange={(gi) => {
              setIllumination(gi.level);
              // Update page data in client store immediately
              const pid = pages.viewingPageId;
              if (pid) {
                const pd = pages.getPageData(pid);
                if (pd) pd.globalIllumination = gi;
              }
              // Sync ref so dynamic fog reads updated value
              globalIlluminationRef.current = gi;
              dynamicFog.invalidateLighting();
              console.warn('[gi-change]', { gi, session: !!mp.session, pageId: pages.viewingPageId });
              if (mp.session) {
                mp.send({
                  type: "page:update-settings",
                  pageId: pages.viewingPageId,
                  changes: { globalIllumination: gi },
                } as any);
              }
            }}
            onDeleteMap={() => {
              if (mp.session) {
                mp.send({
                  type: "map:set",
                  url: "",
                  pageId: pages.viewingPageId,
                } as any);
              }
            }}
          />,
        document.getElementById('zoom-controls')!,
      )}
      <input
        ref={mapInputRef}
        type="file"
        accept="image/*"
        hidden
        onChange={onMapFile}
      />
    </PageOpsProvider>
  );
}
